import type { Debugger } from 'debug'
import Debug from 'debug'
import { action, computed, makeObservable } from 'mobx'

import { insertAtIndex, removeAtIndex, sortedIndex } from '@src/lib'
import shortId from '@src/lib/shortId'

export interface ImmutableCollectionOptions<T extends { [key: string]: any }> {
  /**
   * Specify the unique identifier of the elements.
   *
   * @default id
   */
  readonly idKey?: string

  /**
   * Set a filter function for this collection.
   *
   * If set, the collection will filter its elements.
   */
  filter?: ((item: T) => boolean) | null

  /**
   * Set a comparator function for this collection.
   *
   * If set, the collection will sort its elements.
   */
  compare?: ((a: T, b: T) => number) | null
}

interface VersionedElement<T> {
  element: T
  version: number
}

export default class ImmutableCollection<T extends { [key: string]: any }> {
  protected elements: Map<string, VersionedElement<T>[]> = new Map()
  protected elementIds: string[] = []

  protected debug: Debugger
  protected idKey: string
  protected filter: ((item: T) => boolean) | null = null
  protected compare: ((a: T, b: T) => number) | null = null

  constructor(
    items: readonly T[] | undefined,
    { idKey = 'id', filter = null, compare = null }: ImmutableCollectionOptions<T> = {},
  ) {
    this.idKey = idKey
    this.filter = filter
    this.compare = compare
    this.debug = Debug(`op:collection:${shortId()}`)
    if (items) {
      this._putBulk(items)
    }

    makeObservable<this, '_insertId' | '_put' | '_putBulk'>(this, {
      keys: computed,
      list: computed,
      length: computed,
      _insertId: action,
      _put: action,
      _putBulk: action,
    })
  }

  get list(): T[] {
    return this.elementIds.map((id) => this.getLatestVersion(id) as T)
  }

  get keys(): readonly string[] {
    return this.elementIds
  }

  get length(): number {
    return this.elements.size
  }

  indexOf(item: T | string | null): number {
    if (!item) {
      return -1
    }
    const id = typeof item === 'string' ? item : (item[this.idKey] as string)
    return this.elementIds.indexOf(id)
  }

  find<S extends T>(handler: (item: T) => item is S): S | undefined
  find(handler: (item: T) => boolean): T | undefined
  find(handler: (item: T) => boolean): T | undefined {
    return this.list.find(handler)
  }

  get(id: string, opts: { version?: number } = {}): T | null {
    const versions = this.elements.get(id)
    if (!versions) {
      return null
    }

    if (opts.version === undefined) {
      return this.getLatestVersion(id)
    }

    const versionedElement = versions.find((v) => v.version === opts.version)
    return versionedElement ? versionedElement.element : null
  }

  getLatestVersion(id: string): T | null {
    const versions = this.elements.get(id)
    if (!versions || versions.length === 0) {
      return null
    }

    const latestVersion = versions.reduce((latest, current) =>
      current.version > latest.version ? current : latest,
    )

    return latestVersion.element
  }

  has(id: string, version?: number): boolean {
    const versions = this.elements.get(id)
    if (!versions) {
      return false
    }

    if (version === undefined) {
      return versions.length > 0
    }

    return versions.some((v) => v.version === version)
  }

  protected _put(obj: T, version?: number) {
    if (this.filter && !this.filter(obj)) {
      return
    }

    const id = obj[this.idKey] as string
    const versions = this.elements.get(id) || []
    const versionToUse = version ?? ('version' in obj ? (obj.version as number) : 1)

    // Check if version already exists
    const existingIndex = versions.findIndex((v) => v.version === versionToUse)
    if (existingIndex >= 0) {
      versions[existingIndex] = { element: obj, version: versionToUse }
    } else {
      versions.push({ element: obj, version: versionToUse })
    }

    this.elements.set(id, versions)
    this._insertId(id)
  }

  protected _putBulk(objs: readonly T[]) {
    const { compare, filter } = this

    if (filter) {
      objs = objs.filter((obj: T) => filter(obj))
    }

    // Add all elements without triggering individual sorts
    objs.forEach((obj) => {
      const version = 'version' in obj ? (obj.version as number) : 1
      this._put(obj, version)
    })

    // Sort only once at the end if we have a compare function
    if (compare) {
      this.elementIds.sort((a, b) => {
        const elementA = this.getLatestVersion(a)
        const elementB = this.getLatestVersion(b)
        if (!elementA || !elementB) {
          return 0
        }
        return compare(elementA, elementB)
      })
    }
  }

  protected _insertId(id: string) {
    // Remove the sorting logic from _insertId since we'll handle it in _putBulk
    const oldIndex = this.elementIds.indexOf(id)
    if (oldIndex >= 0) {
      this.elementIds = removeAtIndex(this.elementIds, oldIndex)
    }
    this.elementIds.push(id)
  }
}
